import React, { useState, useEffect } from "react";
import { Flex, Box, Text, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  textPrimary,
  primary,
  white,
  textSecondary,
} from "../../../../utils/colors";
import ParentSize from "@visx/responsive/lib/components/ParentSize";

import useThemeColors from "../../../../hooks/useThemeColors";
import useCountUpTrigger from "../../../../hooks/useCountUpTrigger";
import StatusBarChart from "./StatusBarChart";
import { formatNumberWithCommas } from "../../../../utils/formatUtils";
import PriceBarChart from "./PriceBarChart";

const TreatmentPlansStatsNew = ({
  loading,
  countsByStatus,
  globalTotalCount,
  isFilterActive,
  averagePrice,
  highestServicePrice,
  countsByServiceTotalPrice,
  currencySymbol,
}) => {
  // GENERAL
  const { t } = useTranslation();

  // COLORS
  const { backgroundLightMode } = useThemeColors();

  //STATE
  const [isCountUpReady, setIsCountUpReady] = useState(false);

  const [globalTotal, setGlobalTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentAveragePrice, setCurrentAveragePrice] = useState(null);
  const [currentHighestServicePrice, setCurrentHighestServicePrice] =
    useState(null);

  const [countsByPrice, setCountsByPrice] = useState([]);

  // USE EFFECT

  useEffect(() => {
    if (!loading && countsByStatus) {
      setGlobalTotal(globalTotalCount || 0);
      setTotal(countsByStatus.total || 0);
      setCurrentAveragePrice(averagePrice || 0);
      setCurrentHighestServicePrice(highestServicePrice || 0);

      if (countsByServiceTotalPrice) {
        setCountsByPrice(countsByServiceTotalPrice);
      }

      // Set the flag to start the animation after state has been updated
      setIsCountUpReady(true);
    } else {
      // Reset count-up ready state when loading is true
      setIsCountUpReady(false);
    }
  }, [
    loading,
    countsByStatus,
    globalTotalCount,
    averagePrice,
    highestServicePrice,
    countsByServiceTotalPrice,
  ]);

  const animatedTotal = useCountUpTrigger(total, isCountUpReady);

  // LOGS

  return (
    <Box w="100%" px="2rem" m="0 0 1rem">
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        p="0 0rem 0 0"
        gap="1rem"
      >
        <Flex
          w="25%"
          align="flex-start"
          bg={backgroundLightMode}
          borderRadius="1.25rem"
          direction="column"
          p="1rem"
          minH="140px"
          maxH="140px"
          h="140px"
        >
          <Text fontSize="0.8rem" color={textSecondary} fontWeight="600" m={0}>
            {t("treatmentPlan.statsTotalTreatmentPlans")}
          </Text>

          {!loading ? (
            <Text
              fontSize="1.75rem"
              color={textPrimary}
              fontWeight="600"
              m="1rem 0 0"
              lineHeight="1"
            >
              {animatedTotal}
              {isFilterActive ? (
                <Text
                  as="span"
                  fontWeight="500"
                  fontSize="xs"
                  color={textSecondary}
                >
                  {t("patient.of")} {globalTotal}{" "}
                  {t("treatmentPlan.totalTreatmentPlans")}
                </Text>
              ) : (
                <Text
                  as="span"
                  fontWeight="500"
                  fontSize="xs"
                  color={textSecondary}
                  textTransform="lowercase"
                  ml="0.25rem"
                >
                  {t("treatmentPlan.treatmentPlans")}
                </Text>
              )}
            </Text>
          ) : (
            <Flex p="1rem 0 0.25rem">
              <Spinner size="md" color={primary} />
            </Flex>
          )}

          <Text
            fontSize="xs"
            color={textSecondary}
            fontWeight="400"
            m="0.25rem 0 1rem"
          >
            {t("patient.statsPatientsDescription")}
          </Text>
        </Flex>

        <Flex
          w="25%"
          align="flex-start"
          bg={backgroundLightMode}
          borderRadius="1.25rem"
          direction="column"
          p="1rem"
          minH="140px"
          maxH="140px"
        >
          <Text
            fontSize="0.8rem"
            color={textSecondary}
            fontWeight="600"
            m="0 0 0.5rem"
          >
            {t("treatmentPlan.statsByServicePrice")}
          </Text>
          {!loading ? (
            <Flex w="100%" position="relative">
              <ParentSize>
                {({ width }) => (
                  <PriceBarChart
                    currencySymbol={currencySymbol}
                    data={countsByServiceTotalPrice}
                    width={width}
                    height={80}
                    // showInPrice={showInPrice}
                    // setShowInPrice={setShowInPrice}
                  />
                )}
              </ParentSize>
            </Flex>
          ) : (
            <Flex p="2rem 0" align="center" justify="center" w="100%">
              <Spinner size="md" color={primary} />
            </Flex>
          )}
        </Flex>

        <Flex
          w="25%"
          align="flex-start"
          bg={backgroundLightMode}
          borderRadius="1.25rem"
          direction="column"
          p="1rem"
          minH="140px"
          maxH="140px"
        >
          <Text
            fontSize="0.8rem"
            color={textSecondary}
            fontWeight="600"
            m="0 0 0.5rem"
          >
            {t("treatmentPlan.statsAverage")}
          </Text>

          {!loading &&
          currentAveragePrice !== null &&
          currentHighestServicePrice !== null ? (
            <Flex
              w="100%"
              position="relative"
              align="center"
              justify="space-between"
              gap="1rem"
              p="0.5rem 0 0"
            >
              <Flex w="50%" direction="column">
                <Flex
                  direction="column"
                  bg={white}
                  borderRadius="1rem"
                  align="center"
                  justify="center"
                  p="1rem 0.25rem"
                >
                  <Text
                    fontSize="1.25rem"
                    color={textPrimary}
                    fontWeight="600"
                    lineHeight="1"
                  >
                    {currencySymbol}{" "}
                    {formatNumberWithCommas(currentAveragePrice)}
                  </Text>
                </Flex>

                <Text
                  fontSize="xs"
                  color={textSecondary}
                  fontWeight="400"
                  textAlign="center"
                  m="0.25rem 0 0"
                >
                  {t("treatmentPlan.statsAveragePriceCount")}
                </Text>
              </Flex>

              <Flex w="50%" direction="column">
                <Flex
                  direction="column"
                  bg={white}
                  borderRadius="1rem"
                  align="center"
                  justify="center"
                  p="1rem 0.25rem"
                >
                  <Text
                    fontSize="1.25rem"
                    color={textPrimary}
                    fontWeight="600"
                    lineHeight="1"
                  >
                    {currencySymbol}{" "}
                    {formatNumberWithCommas(currentHighestServicePrice)}
                  </Text>
                </Flex>
                <Text
                  fontSize="xs"
                  color={textSecondary}
                  fontWeight="400"
                  textAlign="center"
                  m="0.25rem 0 0"
                >
                  {t("treatmentPlan.statsHighestPriceCount")}
                </Text>
              </Flex>
            </Flex>
          ) : (
            <Flex p="2rem 0" align="center" justify="center" w="100%">
              <Spinner size="md" color={primary} />
            </Flex>
          )}
        </Flex>

        <Flex
          w="25%"
          align="flex-start"
          bg={backgroundLightMode}
          borderRadius="1.25rem"
          direction="column"
          p="1rem"
          minH="140px"
          maxH="140px"
        >
          <Text
            fontSize="0.8rem"
            color={textSecondary}
            fontWeight="600"
            m="0 0 0.5rem"
          >
            {t("patient.statsByStatus")}
          </Text>

          {!loading ? (
            <Flex w="100%" position="relative">
              <ParentSize>
                {({ width, height }) => (
                  <StatusBarChart
                    width={width}
                    height={80}
                    countsByStatus={countsByStatus}
                  />
                )}
              </ParentSize>
            </Flex>
          ) : (
            <Flex p="2rem 0" align="center" justify="center" w="100%">
              <Spinner size="md" color={primary} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default TreatmentPlansStatsNew;
